import { ApiTypes, StateRecord } from '@mode/shared/contract-common';

import { ModeResourceHelper } from '@mode/shared/util-halogen';
import { AccountResource } from './account.resource';
import { OrganizationResource } from './organization.resource';

export enum SpaceType {
  private = 'private',
  community = 'community',
  custom = 'custom',
}

export interface SpaceResource extends StateRecord, ApiTypes.ModeResource {
  description: string;
  name: string;
  restricted: boolean;
  space_type: SpaceType;
  state: string;
  token: string;
  default_access_level: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace SpaceHelpers {
  import isResource = ModeResourceHelper.isResource;

  export function isCommunitySpace(resource: SpaceResource): boolean {
    return isResource(resource) && resource.space_type === SpaceType.community && resource.name === 'Community';
  }

  export function isRestricted(resource: SpaceResource): boolean {
    return isResource(resource) && resource.restricted;
  }

  export function isRestrictedPersonalSpace(resource: SpaceResource): boolean {
    return isPrivate(resource) && isRestricted(resource);
  }

  export function isPrivate(resource: SpaceResource): boolean {
    return isResource(resource) && resource.space_type === SpaceType.private;
  }

  export function isOpen(resource: SpaceResource): boolean {
    return isResource(resource) && !isRestricted(resource) && !isCommunitySpace(resource);
  }

  export function resolveDisplayName(
    resource: SpaceResource,
    accountFallback: AccountResource,
    report?: any,
    reportContext?: OrganizationResource
  ): string {
    // Space name should be community if it is a community report owned by an organization
    if (report?.public && reportContext && !reportContext.user) {
      return 'Community';
    }
    if ((!isResource(resource) || isPrivate(resource)) && accountFallback && accountFallback.name) {
      return accountFallback.name;
    } else if (isResource(resource)) {
      return resource.name;
    } else {
      return '';
    }
  }

  export function isCreatedBy(space: SpaceResource, account: AccountResource): boolean {
    const creatorLink = isResource(space) && space.hasLink('creator') && space.links('creator').href;
    const accountLink = isResource(account) && account.hasLink('self') && account.links('self').href;

    return creatorLink && accountLink && creatorLink === accountLink;
  }

  export function isPrivateAndNotCreatedBy(space: SpaceResource, account: AccountResource): boolean {
    return isPrivate(space) && !isCreatedBy(space, account);
  }

  export function isMemberOf(space: SpaceResource): boolean {
    return isResource(space) && space.hasLink('user_space_membership');
  }

  export function canLeave(space: SpaceResource): boolean {
    return isRestricted(space) || (isOpen(space) && isMemberOf(space));
  }

  export function canJoin(space: SpaceResource): boolean {
    return isOpen(space) && !isMemberOf(space) && canEditMemberships(space);
  }

  export function canDestroy(space: SpaceResource): boolean {
    return isResource(space) && space.hasForm('destroy');
  }

  export function canCreate(space: SpaceResource): boolean {
    return isResource(space) && space.hasForm('create');
  }

  export function canEdit(space: SpaceResource): boolean {
    return isResource(space) && space.hasForm('edit');
  }

  export function canEditMemberships(space: SpaceResource): boolean {
    return (isRestricted(space) || isOpen(space)) && space.hasEmbedded('new_space_membership');
  }

  export function getCollectionIcon(collection: SpaceResource): string {
    const defaultAccessLevel = collection.default_access_level;
    const spaceType = collection.space_type;
    const lockIcon = 'padlock-icon';
    // community should always be globe, no matter what
    if (spaceType === SpaceType.community) {
      return 'globe-icon';
    }

    if (spaceType === SpaceType.private) {
      return 'user-icon';
    }
    // for collection persmissions, default acccess level is sometimes null or restricted
    // both mean their accessible only by members, so show the lock icon
    if (defaultAccessLevel === 'restricted' || defaultAccessLevel === null) {
      return lockIcon;
    }
    // for old permissions pattern, only show lock icon if a space is 'restricted'
    if (collection.restricted) {
      return lockIcon;
    }
    // else, if you haven't qualified for any of the above conditions, give a normal collection icon
    return 'collection-icon';
  }
}
